import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';

const BASE_URL = process.env.REACT_APP_SERVER_BASE_URL || 'http://localhost:5002';

const ReferralModal = ({ visit, referringDoctorName, onClose, onSubmit, token }) => {
    const [doctors, setDoctors] = useState([]);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [comment, setComment] = useState('');
    const [loadingDoctors, setLoadingDoctors] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const patient = visit?.Patient || {};
    const patientName = `${patient.patient_first_name || ''} ${patient.patient_last_name || ''}`.trim();

    useEffect(() => {
        const fetchDoctors = async () => {
            try {
                const response = await axios.get(
                    `${BASE_URL}/hospital-portal/api/employees/doctors`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const filtered = response.data.filter(
                    (d) => d.doctor_name !== referringDoctorName && d.status !== 'Inactive'
                );
                setDoctors(
                    filtered.map((d) => ({
                        value: d.doctor_name,
                        label: d.doctor_speciality
                            ? `${d.doctor_name} — ${d.doctor_speciality}`
                            : d.doctor_name,
                    }))
                );
            } catch (err) {
                setError('Could not load doctor list. Please try again.');
            } finally {
                setLoadingDoctors(false);
            }
        };
        fetchDoctors();
    }, [token, referringDoctorName]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedDoctor?.value || !comment.trim()) return;
        setSubmitting(true);
        setError('');
        try {
            await onSubmit(selectedDoctor.value, comment.trim());
        } catch (err) {
            setError(err?.message || 'Failed to create referral. Please try again.');
            setSubmitting(false);
        }
    };

    const canSubmit = selectedDoctor?.value && comment.trim().length > 0 && !submitting;

    return (
        <div className="lab-request-modal-overlay" onClick={onClose}>
            <div
                className="lab-request-modal-content referral-modal-content"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="referral-modal-header">
                    <h3>Refer Patient</h3>
                    <button
                        className="referral-modal-close"
                        onClick={onClose}
                        type="button"
                        aria-label="Close referral dialog"
                    >
                        &times;
                    </button>
                </div>

                <div className="referral-modal-patient">
                    Referring: <strong>{patientName || 'Unknown Patient'}</strong>
                    {patient.patient_number && (
                        <span className="referral-patient-number"> &middot; #{patient.patient_number}</span>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="referral-modal-form">
                    <div className="referral-field">
                        <label htmlFor="referral-doctor">Refer To Doctor <span className="required-star">*</span></label>
                        {loadingDoctors ? (
                            <p className="referral-loading">Loading doctors...</p>
                        ) : (
                            <Select
                                inputId="referral-doctor"
                                options={doctors}
                                value={selectedDoctor}
                                onChange={(option) => setSelectedDoctor(option)}
                                isSearchable
                                isClearable
                                placeholder="Search doctor..."
                                isDisabled={submitting}
                                noOptionsMessage={() => 'No doctors found'}
                                classNamePrefix="referral-select"
                            />
                        )}
                    </div>

                    <div className="referral-field">
                        <label htmlFor="referral-comment">Referral Notes <span className="required-star">*</span></label>
                        <textarea
                            id="referral-comment"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Enter reason for referral and any relevant clinical notes..."
                            rows={4}
                            required
                            disabled={submitting}
                        />
                    </div>

                    {error && <p className="referral-error">{error}</p>}

                    <div className="referral-modal-actions">
                        <button type="button" className="referral-btn-cancel" onClick={onClose} disabled={submitting}>
                            Cancel
                        </button>
                        <button type="submit" className="referral-btn-submit" disabled={!canSubmit}>
                            {submitting ? 'Referring...' : 'Confirm Referral'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ReferralModal;
